import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["data-index"]

import { reactive, onMounted } from 'vue'
import { useEventBus } from '@vueuse/core'
import { gsap } from 'gsap'
import { MJPSectionData } from '../magicjohnsonpark/types'

enum AudioChannelState {
  Dormant = 'dormant',
  FadingIn = 'fadingIn',
  Playing = 'playing',
  FadingOut = 'fadingOut',
}

interface AudioManagerState {
  firstAutoplayCheckDone: boolean
  currentSection: string
  channels: Array<AudioChannel>
}

interface Props {
  sectionAudio: object
  channels: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AudioManager',
  props: {
    sectionAudio: null,
    channels: null
  },
  setup(__props: any) {

const props = __props as {
  sectionAudio: object
  channels: number
}

class AudioChannel {
  filename: string
  state: AudioChannelState
  element: HTMLAudioElement | null
  isDucked: boolean
  isMobile: boolean
  isMuted: boolean
  constructor() {
    this.filename = ''
    this.state = AudioChannelState.Dormant
    this.element = null
    this.isDucked = false
    this.isMobile = window.innerWidth < 768
    this.isMuted = window.innerWidth < 768
  }
  setState(state) {
    this.element?.setAttribute('data-state', state) // helps with debugging
    this.state = state
  }
  getMaxVolume() {
    if (this.isDucked) {
      return 0
    } else {
      return 0.6
    }
  }
  handleFadeInComplete() {
    this.setState(AudioChannelState.Playing)
  }
  handleFadeOutComplete() {
    this.element?.pause()
    this.setState(AudioChannelState.Dormant)
  }
  fadeIn(filename, duration) {
    if (this.element) {
      const isSameFile = this.filename == filename
      let startingVolume = 0
      if (!isSameFile) {
        this.filename = filename
        this.element.src = `/audio/${this.filename}`
        if (!this.isMuted) this.element.play()
      } else if (this.state == AudioChannelState.Dormant) {
        startingVolume = this.element.volume
        this.element.currentTime = 0
        if (!this.isMuted) this.element.play()
      }
      if (!this.isMobile) {
        this.setState(AudioChannelState.FadingIn)
        gsap.killTweensOf(this.element)
        gsap.fromTo(
          this.element,
          {
            volume: startingVolume,
          },
          {
            volume: this.getMaxVolume(),
            duration: duration,
            callbackScope: this,
            onComplete: this.handleFadeInComplete,
          }
        )
      } else {
        // can't programmatically change volume on iOS
        this.element.volume = this.getMaxVolume()
        this.handleFadeInComplete()
      }
    }
  }
  fadeOut(duration) {
    if (!this.isMobile) {
      this.setState(AudioChannelState.FadingOut)
      gsap.killTweensOf(this.element)
      gsap.to(this.element, {
        volume: 0,
        duration: duration,
        callbackScope: this,
        onComplete: this.handleFadeOutComplete,
      })
    } else {
      // can't programmatically change volume on iOS
      this.handleFadeOutComplete()
    }
  }
  duck(isDucked = true) {
    this.isDucked = isDucked
    if (
      this.state != AudioChannelState.Dormant &&
      this.state != AudioChannelState.FadingOut
    ) {
      this.state = AudioChannelState.Playing
      if (!this.isMobile) {
        gsap.killTweensOf(this.element)
        gsap.to(this.element, {
          volume: this.getMaxVolume(),
          duration: 1,
          callbackScope: this,
        })
      } else {
        if (this.isDucked) {
          this.element?.pause()
        } else {
          this.element?.play()
        }
      }
    }
  }
  mute(isMuted = true) {
    this.isMuted = isMuted
    if (this.state != AudioChannelState.Dormant) {
      if (this.isMuted) {
        this.element?.pause()
      } else {
        this.element?.play()
      }
    }
  }
}



const sectionChangedBus = useEventBus('section-changed')
const setAudioMutedBus = useEventBus('set-audio-muted')
const setAudioDuckedBus = useEventBus('set-audio-ducked')
const videoPlayerOpenedBus = useEventBus('video-opened')
const videoPlayerClosedBus = useEventBus('video-closed')
const state: AudioManagerState = reactive({
  firstAutoplayCheckDone: false,
  currentSection: 'none',
  channels: [],
})

videoPlayerOpenedBus.on(() => {
  duckAllChannels(true)
})

videoPlayerClosedBus.on(() => {
  duckAllChannels(false)
})

sectionChangedBus.on((sectionData: unknown) => {
  const sectionDataProxy = sectionData as MJPSectionData
  if (sectionDataProxy.section != state.currentSection) {
    if (props.sectionAudio[sectionDataProxy.section].ambience) {
      startAmbience(props.sectionAudio[sectionDataProxy.section].ambience)
    } else {
      fadeOutAllChannelsExcept(null, 6)
    }
    state.currentSection = sectionDataProxy.section
  }
})

const startAmbience = (filename: string) => {
  const channel = getOpenChannel(filename)
  if (channel) {
    channel.fadeIn(filename, 6)
    fadeOutAllChannelsExcept(channel, 6)
  }
}

setAudioMutedBus.on((isMuted: unknown) => {
  const isMutedProxy = isMuted as boolean
  let channel
  for (let i = 0; i < state.channels.length; i++) {
    channel = state.channels[i]
    channel.mute(isMutedProxy)
  }
})

setAudioDuckedBus.on((isDucked: unknown) => {
  const isDuckedProxy = isDucked as boolean
  let channel
  for (let i = 0; i < state.channels.length; i++) {
    channel = state.channels[i]
    channel.duck(isDuckedProxy)
  }
})

const getOpenChannel = (filename) => {
  let channel
  for (let i = 0; i < state.channels.length; i++) {
    channel = state.channels[i]
    if (
      channel.state == AudioChannelState.Dormant ||
      channel.filename == filename
    ) {
      return channel
    }
  }
  return null
}

const duckAllChannels = (isDucked = true) => {
  let channel
  for (let i = 0; i < state.channels.length; i++) {
    channel = state.channels[i]
    channel.duck(isDucked)
  }
}

const fadeOutAllChannelsExcept = (
  activeChannel: AudioChannel | null,
  duration
) => {
  let channel
  for (let i = 0; i < state.channels.length; i++) {
    channel = state.channels[i]
    if (
      channel != activeChannel &&
      channel.state != AudioChannelState.Dormant
    ) {
      channel.fadeOut(duration)
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const handleTimeUpdate = () => {
  if (!state.firstAutoplayCheckDone) {
    setAudioMutedBus.emit(false)
    state.firstAutoplayCheckDone = true
  }
}

onMounted(() => {
  for (let i = 0; i < props.channels; i++) {
    state.channels.push(new AudioChannel())
  }
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(state.channels, (channel, index) => {
      return (_openBlock(), _createElementBlock("audio", {
        ref_for: true,
        ref: (el) => (channel.element = el as HTMLAudioElement),
        key: index,
        "data-index": index,
        class: "invisible",
        autoplay: "",
        onTimeupdate: handleTimeUpdate
      }, null, 40, _hoisted_1))
    }), 128))
  ]))
}
}

})